import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import environment from '../../../common/environment';
import { StoreState } from '../../../store/store.interface';
import { fetchUserProfile } from '../../user-profile/user-profile.thunks';
import { selectUser, isSuccessUser, getAnalyticsSafeData } from '../../user-profile/user-profile.selectors';
import { isHEInstructor } from '../../../common/utils/cart-util';
import { UserProfile } from '../../user-profile/user-profile.interface';
import emit from '../../../common/events';
import { getAppFlow } from '../../user-profile/utils/user-profile.util';

const ORG_XID_REGEX = /organization:([a-f0-9-]{36})$/;

@customElement('mhecomm-my-learning-technology-rep')
export class MyLearningTechnologyRep extends connect(store)(BaseComponent) {

  @state()
  private user: UserProfile | undefined;

  @state()
  private isHEInstructorUser = false;

  @state()
  private findMyLTRUrl: string = this.getFindMyLTRUrl();

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    store.dispatch(fetchUserProfile());
  }

  stateChanged(state: StoreState) {
    if (!isSuccessUser(state)) {
      this.isHEInstructorUser = false;
      return;
    }
    const user: UserProfile | undefined = selectUser(state);
    if (!user) {
      this.isHEInstructorUser = false;
      return;
    }
    this.user = user;
    this.isHEInstructorUser = isHEInstructor(user);
    this.findMyLTRUrl = this.getFindMyLTRUrl(user.affiliations?.[0]?.orgXid);
  }

  private getFindMyLTRUrl(orgXid?: string): string {
    const baseUrl = environment().mheducationUrl;
    if (orgXid) {
      const match = ORG_XID_REGEX.exec(orgXid);
      if (match) {
        return `${baseUrl}/highered/support/find-your-rep/ltr-list.html?schoolId=${match[1]}`;
      }
    }
    return `${baseUrl}/highered/support/find-your-rep.html`;
  }

  private _onFindMyLtrClick = (_e: Event) => {
    emit({
      element: this,
      name: 'click',
      detail: {
        id: 'my-account-find-ltr-button',
        label: 'Find my LTR',
        comp: 'button',
        href: this.findMyLTRUrl,
        user: getAnalyticsSafeData(this.user!, true),
        school: {
          orgXid: this.user?.affiliations?.[0]?.orgXid,
          orgName: this.user?.affiliations?.[0]?.orgName,
          orgCountry: this.user?.affiliations?.[0]?.orgCountry,
          orgState: this.user?.affiliations?.[0]?.orgState,
          orgCity: this.user?.affiliations?.[0]?.orgCity,
          segment: this.user?.affiliations?.[0]?.segment,
        },
        appFlow: getAppFlow(),
        myAccountSection: 'My Learning Technology Rep',
      },
    });
  };

  renderComp() {
    if (!this.isHEInstructorUser) return nothing;

    return html`
      <div class="my-learning-technology-rep row g-4">
        <div class="col-12 col-lg-6">
          <h2>My Learning Technology Rep</h2>
          <p>Your Learning Technology Representative will guide you in selecting the best products to meet your goals and help your students succeed.</p>
          <a
            href=${this.findMyLTRUrl}
            class="btn btn-outline-primary"
            data-automation-id="find-my-ltr-link"
            @click=${this._onFindMyLtrClick}
            @keyup=${(e: KeyboardEvent) => e.code === 'Space' || e.code === 'Enter' ? this._onFindMyLtrClick(e) : nothing}
          >
            Find my LTR
          </a>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-my-learning-technology-rep': MyLearningTechnologyRep;
  }
}
